import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { DEFAULT_PAGE } from '~/utils/constants/common';
import { ISmsConversation, ISmsMessage, IContactDetails } from '~/apis/sms';
import {
  handleGetSmsConversations,
  handleGetSmsMessages,
  handleSendSms,
  handleUpdateSmsConversation,
  handleGetSmsContactDetails,
} from './smsThunk';

export interface SmsState {
  conversations: ISmsConversation[];
  messages: ISmsMessage[];
  selectedConversation: ISmsConversation | null;
  contactDetails: IContactDetails | null;

  // Pagination for conversations
  conversationsCurrentPage: number;
  conversationsTotalPage: number;
  conversationsTotalItems: number;

  // Pagination for messages
  messagesCurrentPage: number;
  messagesTotalPage: number;
  messagesTotalItems: number;

  // Loading states
  conversationsLoading: boolean;
  messagesLoading: boolean;
  sendingSms: boolean;
  contactDetailsLoading: boolean;

  // Refresh flag
  needRefreshConversations: boolean;
  needRefreshMessages: boolean;

  // Filter state
  filterParams: {
    status: 'active' | 'archived';
    search: string;
    assignedTo: string;
    salesRep: string;
  };
}

const initialState: SmsState = {
  conversations: [],
  messages: [],
  selectedConversation: null,
  contactDetails: null,

  conversationsCurrentPage: DEFAULT_PAGE,
  conversationsTotalPage: 0,
  conversationsTotalItems: 0,

  messagesCurrentPage: DEFAULT_PAGE,
  messagesTotalPage: 0,
  messagesTotalItems: 0,

  conversationsLoading: false,
  messagesLoading: false,
  sendingSms: false,
  contactDetailsLoading: false,

  needRefreshConversations: false,
  needRefreshMessages: false,

  filterParams: {
    status: 'active',
    search: '',
    assignedTo: '',
    salesRep: '',
  },
};

const smsSlice = createSlice({
  name: 'sms',
  initialState,
  reducers: {
    setNeedRefreshConversations(state, action: PayloadAction<boolean>) {
      state.needRefreshConversations = action.payload;
    },
    setNeedRefreshMessages(state, action: PayloadAction<boolean>) {
      state.needRefreshMessages = action.payload;
    },
    setSelectedConversation(state, action: PayloadAction<ISmsConversation | null>) {
      state.selectedConversation = action.payload;
    },
    setFilterParams(state, action: PayloadAction<Partial<SmsState['filterParams']>>) {
      state.filterParams = { ...state.filterParams, ...action.payload };
    },
    clearMessages(state) {
      state.messages = [];
    },
  },
  extraReducers(builder) {
    builder
      // Get Conversations
      .addCase(handleGetSmsConversations.pending, (state) => {
        state.conversationsLoading = true;
      })
      .addCase(handleGetSmsConversations.fulfilled, (state, action) => {
        state.conversations = action.payload.conversations;
        state.conversationsCurrentPage = action.payload.pagination.page;
        state.conversationsTotalPage = action.payload.pagination.totalPages;
        state.conversationsTotalItems = action.payload.pagination.total;
        state.conversationsLoading = false;
        state.needRefreshConversations = false;
      })
      .addCase(handleGetSmsConversations.rejected, (state) => {
        state.conversationsLoading = false;
      })

      // Get Messages
      .addCase(handleGetSmsMessages.pending, (state) => {
        state.messagesLoading = true;
      })
      .addCase(handleGetSmsMessages.fulfilled, (state, action) => {
        state.messages = action.payload.messages;
        state.messagesCurrentPage = action.payload.pagination.page;
        state.messagesTotalPage = action.payload.pagination.totalPages;
        state.messagesTotalItems = action.payload.pagination.total;
        state.messagesLoading = false;
        state.needRefreshMessages = false;
      })
      .addCase(handleGetSmsMessages.rejected, (state) => {
        state.messagesLoading = false;
      })

      // Send SMS
      .addCase(handleSendSms.pending, (state) => {
        state.sendingSms = true;
      })
      .addCase(handleSendSms.fulfilled, (state) => {
        state.sendingSms = false;
        state.needRefreshConversations = true;
        state.needRefreshMessages = true;
      })
      .addCase(handleSendSms.rejected, (state) => {
        state.sendingSms = false;
      })

      // Update Conversation
      .addCase(handleUpdateSmsConversation.fulfilled, (state) => {
        state.needRefreshConversations = true;
      })

      // Get Contact Details
      .addCase(handleGetSmsContactDetails.pending, (state) => {
        state.contactDetailsLoading = true;
      })
      .addCase(handleGetSmsContactDetails.fulfilled, (state, action) => {
        state.contactDetails = action.payload;
        state.contactDetailsLoading = false;
      })
      .addCase(handleGetSmsContactDetails.rejected, (state) => {
        state.contactDetailsLoading = false;
      });
  },
});

export const smsActions = smsSlice.actions;

const smsReducer = smsSlice.reducer;
export default smsReducer;
